/**
 * Accessible 6-digit PIN input with numeric keypad.
 *
 * Visual: filled/empty dots showing progress, numeric keypad below.
 * Accessibility (A11Y-07): aria-live region announces digit progress,
 * each button has aria-label, delete button labeled, error is assertive.
 * Touch targets: minimum 44x44px per button.
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module PinEntry
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

/** Props for PinEntry component. */
interface PinEntryProps {
  /** Number of digits in the PIN (default 6). */
  readonly length?: number;
  /** Called when all digits have been entered. */
  readonly onComplete: (pin: string) => void;
  /** Error message to display (e.g., "Incorrect PIN"). */
  readonly error?: string;
  /** Title shown above the dots (e.g., "Enter your PIN"). */
  readonly title: string;
  /** Whether the component is in a loading state. */
  readonly loading?: boolean;
}

/** Keypad digit layout: rows of 3. */
const KEYPAD_ROWS: ReadonlyArray<ReadonlyArray<string | null>> = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  [null, '0', 'delete'],
];

/**
 * Accessible 6-digit PIN entry with on-screen numeric keypad.
 *
 * Supports both on-screen keypad taps and physical keyboard input.
 * Calls onComplete immediately when the last digit is entered.
 *
 * Tier: All tiers (core infrastructure).
 */
export function PinEntry({
  length = 6,
  onComplete,
  error,
  title,
  loading = false,
}: PinEntryProps): React.ReactElement {
  const [digits, setDigits] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const submitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Autofocus on mount so keyboard input works immediately
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  // Cleanup submit timer on unmount
  useEffect(() => {
    return () => {
      if (submitTimerRef.current !== null) {
        clearTimeout(submitTimerRef.current);
      }
    };
  }, []);

  // Reset digits when error changes (user needs to re-enter)
  useEffect(() => {
    if (error) {
      setDigits('');
    }
  }, [error]);

  const addDigit = useCallback(
    (digit: string): void => {
      if (loading) return;

      setDigits((prev) => {
        if (prev.length >= length) return prev;
        const next = prev + digit;
        if (next.length === length) {
          // Delay onComplete so the 6th dot fills visually before submit
          submitTimerRef.current = setTimeout(() => {
            onComplete(next);
          }, 150);
        }
        return next;
      });
    },
    [length, onComplete, loading],
  );

  const removeDigit = useCallback((): void => {
    if (loading) return;
    setDigits((prev) => prev.slice(0, -1));
  }, [loading]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent): void => {
      if (event.key >= '0' && event.key <= '9') {
        event.preventDefault();
        addDigit(event.key);
      } else if (event.key === 'Backspace') {
        event.preventDefault();
        removeDigit();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        // Submit if PIN is complete (all digits entered)
        if (digits.length === length) {
          onComplete(digits);
        }
      }
    },
    [addDigit, removeDigit, digits, length, onComplete],
  );

  const handleKeypadPress = useCallback(
    (key: string): void => {
      if (key === 'delete') {
        removeDigit();
      } else {
        addDigit(key);
      }
    },
    [addDigit, removeDigit],
  );

  return (
    <div
      ref={containerRef}
      className="flex flex-col items-center gap-8"
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="group"
      aria-label={title}
    >
      {/* Title */}
      <h2 className="text-xl font-semibold text-foreground">{title}</h2>

      {/* Progress dots */}
      <div className="flex gap-3" aria-hidden="true">
        {Array.from({ length }, (_, i) => (
          <div
            key={i}
            className={cn(
              'h-4 w-4 rounded-full transition-all duration-fast ease-default',
              i < digits.length
                ? 'bg-primary scale-110'
                : i === digits.length
                  ? 'bg-muted ring-2 ring-primary/50'
                  : 'bg-muted',
            )}
          />
        ))}
      </div>

      {/* Screen reader progress announcement */}
      <div className="sr-only" aria-live="polite" role="status">
        {digits.length} of {length} digits entered
      </div>

      {/* Error message */}
      {error && (
        <p className="text-sm font-medium text-destructive" aria-live="assertive" role="alert">
          {error}
        </p>
      )}

      {/* Numeric keypad */}
      <div className="flex flex-col gap-3">
        {KEYPAD_ROWS.map((row, rowIndex) => (
          <div key={rowIndex} className="flex justify-center gap-3">
            {row.map((key, colIndex) => {
              if (key === null) {
                return <div key={colIndex} className="h-16 w-16" aria-hidden="true" />;
              }

              if (key === 'delete') {
                return (
                  <button
                    key={colIndex}
                    type="button"
                    className={cn(
                      'flex h-16 w-16 items-center justify-center',
                      'rounded-full text-foreground',
                      'transition-colors duration-fast ease-default',
                      'hover:bg-secondary active:bg-secondary/70',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      'disabled:opacity-50 disabled:pointer-events-none',
                    )}
                    onClick={() => handleKeypadPress(key)}
                    aria-label="Delete last digit"
                    disabled={loading || digits.length === 0}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                      <line x1="18" y1="9" x2="12" y2="15" />
                      <line x1="12" y1="9" x2="18" y2="15" />
                    </svg>
                  </button>
                );
              }

              return (
                <button
                  key={colIndex}
                  type="button"
                  className={cn(
                    'flex h-16 w-16 items-center justify-center',
                    'rounded-full text-lg font-semibold text-foreground',
                    'transition-colors duration-fast ease-default',
                    'hover:bg-secondary active:bg-secondary/70',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    'disabled:opacity-50 disabled:pointer-events-none',
                  )}
                  onClick={() => handleKeypadPress(key)}
                  aria-label={`Digit ${key}`}
                  disabled={loading}
                >
                  {key}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export type { PinEntryProps };
